import { useCallback, useEffect, useRef } from 'react'
import { useBlocker } from 'react-router'

import { useUI } from '@evertel/web/ui'

import { confirmUnsavedChanges } from './confirmUnsavedChanges'

/**
 * Prompts before leaving a page with unsaved changes, regardless of how the
 * navigation is triggered: in-app <Link>s, navigate() calls and browser
 * back/forward (via react-router's useBlocker), plus tab close/refresh
 * (via beforeunload).
 *
 * Returns `allowNavigation()`. Call it immediately before a programmatic
 * navigation that must NOT prompt (e.g. redirecting after a successful save).
 * This is required because `setHasChanged(false)` followed by `navigate()` in
 * the same tick has not re-rendered yet, so the blocker would still see
 * `hasChanged === true`.
 */
const useUnsavedChangesGuard = (hasChanged: boolean) => {
    const { isConfirmed } = useUI()

    // Refs so the blocker predicate always reads the latest values
    const hasChangedRef = useRef(hasChanged)
    hasChangedRef.current = hasChanged
    const bypassRef = useRef(false)
    const promptingRef = useRef(false)

    // New edits after a bypass should be guarded again
    useEffect(() => {
        if (hasChanged) bypassRef.current = false
    }, [hasChanged])

    const allowNavigation = useCallback(() => {
        bypassRef.current = true
    }, [])

    const blocker = useBlocker(useCallback(({ currentLocation, nextLocation }) => {
        if (bypassRef.current || !hasChangedRef.current) return false
        // Only guard real page changes, not search/hash-only updates
        return currentLocation.pathname !== nextLocation.pathname
    }, []))

    useEffect(() => {
        if (blocker.state !== 'blocked' || promptingRef.current) return

        const { proceed, reset } = blocker
        promptingRef.current = true

        confirmUnsavedChanges(isConfirmed)
            .then((confirmed) => (confirmed ? proceed() : reset()))
            .finally(() => { promptingRef.current = false })
    }, [blocker, isConfirmed])

    // Tab close / refresh / external URL
    useEffect(() => {
        if (!hasChanged) return

        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (bypassRef.current) return
            e.preventDefault()
            // Legacy browsers require a returnValue to show the prompt
            e.returnValue = ''
        }

        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [hasChanged])

    return { allowNavigation }
}

export { useUnsavedChangesGuard }
